/** @format */

import { Table, TableFooter } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import Filter from '../../interfaces/forms/Filter';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import TableBody from '@material-ui/core/TableBody/index';
import TableCell from '@material-ui/core/TableCell/index';
import TableHead from '@material-ui/core/TableHead/index';
import TableRow from '@material-ui/core/TableRow/index';
import { configDefault } from '../../core/config';
import { DateHelper } from '../../helpers/DateHelper';
import Order from '../../interfaces/forms/Order';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import EditButton from '../../components/Buttons/EditButton';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { FilterReducerFieldName } from '../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { NumberRange } from '../../interfaces/platform/NumberRange';
import { NumberRangeCollectionInterface } from '../../interfaces/collections/NumberRangeCollectionInterface';
import { numberRangeConstants, numberRangeReducerType } from '../../modules/numberRange/reducers';

const useStyle = makeStyles(adescomStyle);

interface NumberRangeListProps {
    platformResellerId: number;
    editExistingRange: (numberRange: NumberRange) => void;
}

const NumberRangeList = (props: NumberRangeListProps): React.ReactElement => {
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'platformResellerId', value: props.platformResellerId }]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const dispatch = useDispatch();

    const { type, collections } = useSelector((state: { numberRangeReducers: numberRangeReducerType }) => {
        return state.numberRangeReducers;
    });

    const classes = useStyle();
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };
    const tableStickyHeader = { root: classes.tableStickyHeader };

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/number_ranges', configDefault.PLATFORM_API_PREFIX)),
            numberRangeConstants.NUMBER_RANGE_LIST,
            filters,
            orders,
            (data: NumberRangeCollectionInterface) => {
                dispatch({
                    type: numberRangeConstants.NUMBER_RANGE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, filters, orders]);

    const updateOrderList = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilterList = (filters: Array<Filter>): void => {
        setFilters(filters);
    };
    const editNumberRange = (params: { numberRange: NumberRange }): void => {
        props.editExistingRange(params.numberRange);
    };
    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath('/number_ranges', configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/number_ranges', configDefault.PLATFORM_API_PREFIX)),
            numberRangeConstants.NUMBER_RANGE_LIST,
            filters,
            orders,
            (data: NumberRangeCollectionInterface) => {
                dispatch({
                    type: numberRangeConstants.NUMBER_RANGE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const clearFilter = (): void => {
        setFilters([{ name: 'platformResellerId', value: props.platformResellerId }]);
        setOrders([]);

        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableControlStickyHeader} colSpan={5} align="left" padding="none">
                        {(filters.length > 0 || orders.length > 0) && (
                            <CustomTextButtom callback={clearFilter} callbackParam={{}} label="general:common.clear" />
                        )}
                        <CustomTextButtom callback={paginationCallbackFunction} label="general:common.search" />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableHead>
                <TableRow>
                    <TableCell classes={tableControlStickyHeader}>
                        <TableHeaderContentCell textAlign="center" title="general:common.commands" showSort={false} onlyFilter={false} />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="number_range:h.start_number"
                            fieldName="numberStart"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="string"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="number_range:h.end_number"
                            fieldName="numberEnd"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="string"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="number_range:h.activated_at"
                            fieldName="activatedAt"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="date"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                        />
                    </TableCell>
                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                        <TableHeaderContentCell
                            textAlign="left"
                            title="number_range:h.created_at"
                            fieldName="createdAt"
                            showSort={true}
                            onlyFilter={false}
                            defaultDirection="desc"
                            searchFieldType="date"
                            filters={filters}
                            sort={orders}
                            updateOrderList={updateOrderList}
                            updateFilterList={updateFilterList}
                        />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {collections &&
                    collections['hydra:member'].map((el: NumberRange) => {
                        return (
                            <TableRow key={el.id}>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    <EditButton callback={editNumberRange} callbackParam={{ numberRange: el }} />
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {el.numberStart}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {el.numberEnd}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {DateHelper.formatYYYYMMDD(el.createdAt)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} padding="none" align="center">
                                    {DateHelper.formatYYYYMMDD(el.activatedAt)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
            </TableBody>
            <TableFooter></TableFooter>
        </Table>
    );
};

export default NumberRangeList;
